<template>
  <ion-page>
    <main-header></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <div v-if="!orders.length">
          <div class="ion-margin-bottom">
            No order history to show. This means you must be hungry and thirsty!
            time to start an order without leaving your seat.
          </div>
          <ion-button
            @click="handlePlaceAnOrder()"
            expand="block"
            data-cy="handle-place-order-button"
          >
            Place an Order
          </ion-button>
        </div>
        <div v-else>
          <ion-card
            v-if="!orderLocation"
            color="danger"
            button
            @click="handleLocationPicker()"
          >
            <ion-card-content>
              To enable re-order functionality, please choose your current
              location.
            </ion-card-content>
          </ion-card>
          <ion-card
            v-for="order in openOrders"
            :key="`open-order-${order.id}`"
            :data-cy="`open-order-${order.id}`"
            color="primary"
            button
            @click="handleOrderDetails(order.id)"
          >
            <ion-card-header color="dark">
              <ion-card-title>
                Current Order
                <ion-badge class="ion-float-right">
                  {{ order.status | ucfirst }}
                </ion-badge>
              </ion-card-title>
            </ion-card-header>
            <ion-card-content v-if="order.isDelivery()">
              <ion-avatar
                v-if="order.status == ORDER_DELIVERING"
                class="ion-float-right"
              >
                <img
                  v-if="runnerImageUrl(order)"
                  :src="runnerImageUrl(order)"
                />
              </ion-avatar>

              <p v-if="order.status == ORDER_DELIVERING">
                <strong>Runner:</strong>
              </p>
              <p v-if="order.status == ORDER_DELIVERING">
                {{ runnerName(order) }}
              </p>
              <p><strong>Deliver to:</strong></p>
              <p>
                Section {{ order.section }}, Row {{ order.row }}, Seat
                {{ order.seat }}
              </p>
              <p v-if="order.instructions">
                <em>{{ order.instructions }}</em>
              </p>
            </ion-card-content>
            <ion-card-content v-else>
              <p><strong>Pickup Instructions:</strong></p>
              <p>{{ order.pickupInstructions }}</p>
            </ion-card-content>
          </ion-card>
          <ion-list data-cy="order-history-list">
            <span v-for="(order, idx) in orders" :key="`order-${order.id}`">
              <ion-item
                detail
                button
                @click="handleOrderDetails(order.id)"
                :lines="canOrderAgain(order, idx) ? 'none' : 'inset'"
              >
                <ion-label>
                  {{ order.locationName }}
                  <p>
                    {{
                      order.requested ? order.requested.toLocaleString() : ""
                    }}
                  </p>
                </ion-label>
                {{ order.totalPrice | usd }}
              </ion-item>
              <ion-item v-if="canOrderAgain(order, idx)">
                <ion-button slot="end" @click="orderAgain(order)">
                  Order Again
                </ion-button>
              </ion-item>
            </span>
          </ion-list>
        </div>
      </div>
      <ion-infinite-scroll threshold="100px" id="infinite-scroll">
        <ion-infinite-scroll-content
          loading-spinner="lines"
          loading-text="Loading more data..."
        >
        </ion-infinite-scroll-content>
      </ion-infinite-scroll>
    </ion-content>
  </ion-page>
</template>

<script>
import errorAlert from "@/ErrorAlert";
import { mapGetters, mapMutations } from "vuex";
import Order from "@/models/Order";
import {
  ORDER_OPEN,
  ORDER_PREPARING,
  ORDER_DELIVERING,
  ORDER_COMPLETED
} from "@/constants/order";
import filterOrder from "@/mixins/filter-order";
import UserModelService from "@/services/UserModel";

let lastVisible;
const pageSize = 10;
const ordersThatCanReOrder = 7;

export default {
  mixins: [filterOrder],

  title: "Order History",

  data: function() {
    return {
      loading: true,
      orders: [],
      users: {},
      noMoreResults: false,
      ORDER_OPEN,
      ORDER_PREPARING,
      ORDER_DELIVERING
    };
  },

  created() {
    this.appendOrderHistory().then(() => {
      this.loading = false;
    });
  },

  mounted() {
    const infiniteScroll = document.getElementById("infinite-scroll");

    infiniteScroll.addEventListener("ionInfinite", async () => {
      this.appendOrderHistory().then(() => {
        if (this.noMoreResults) {
          infiniteScroll.disabled = true;
        } else {
          infiniteScroll.complete();
        }
      });
    });
  },

  beforeDestroy() {
    lastVisible = null;
  },

  methods: {
    ...mapMutations("order", ["addItemToOrder"]),

    handlePlaceAnOrder() {
      this.$router.push({ name: "App: Location Menu" }).catch(e => e);
    },

    handleOrderDetails(id) {
      this.$router
        .push({ name: "App: Order History Detail", params: { id } })
        .catch(e => e);
    },

    appendOrderHistory() {
      let query = this.$firestore()
        .collection("orders")
        .withConverter(Order.firebaseConverter)
        .where("userId", "==", this.user.uid)
        .orderBy("requested", "desc");

      if (lastVisible) {
        query = query.startAfter(lastVisible);
      }

      return query
        .limit(pageSize)
        .get()
        .then(querySnapshot => {
          let newUsers = [];

          if (querySnapshot.size === 0) {
            this.noMoreResults = true;
          } else {
            lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
            querySnapshot.forEach(doc => {
              let order = doc.data();
              this.orders.push(order);
              if (
                order.deliveringUserId &&
                !this.users[order.deliveringUserId]
              ) {
                newUsers.push(order.deliveringUserId);
              }
            });
          }

          return newUsers;
        })
        .then(newUsers => {
          if (newUsers.length) {
            return UserModelService.getUsersByIdsKeyedById(newUsers).then(
              newlyFoundUsers => {
                this.users = { ...this.users, ...newlyFoundUsers };
              }
            );
          }
        })
        .catch(() => {
          // errorAlert("Unable to retrieve your history at this time.");
        });
    },

    runnerName(order) {
      let name = "Stay Seated Staff";
      if (this.users[order.deliveringUserId]) {
        if (this.users[order.deliveringUserId].displayName) {
          name = this.users[order.deliveringUserId].displayName;
        }
      }
      return name;
    },

    runnerImageUrl(order) {
      let url = null;
      if (this.users[order.deliveringUserId]) {
        if (this.users[order.deliveringUserId].photoURL) {
          url = this.users[order.deliveringUserId].photoURL;
        }
      }
      return url;
    },

    /**
     * if the location is picked, and this order is within the top x orders
     */
    canOrderAgain(order, idx) {
      return (
        Boolean(this.orderLocation) &&
        order.locationId == this.orderLocation.id &&
        idx < ordersThatCanReOrder &&
        order.status === ORDER_COMPLETED
      );
    },

    /**
     * We're gonna assume that if they're here, they got here from a valid canOrderAgain()
     *
     * Also, I fully realize we should probably make the order stuff into some sort of service
     * so that code is not duplicated. But, time constraints...
     */
    async orderAgain(order) {
      this.loading = true;

      let newOrderItems;

      try {
        newOrderItems = await this.filterOrderItemsByAvailable(
          order.items,
          order.locationId
        );
      } catch {
        return errorAlert("We were unable to create this order again.");
      }

      if (newOrderItems.length !== order.items.length) {
        this.infoToast(
          "There were some items in this order that are no longer available."
        );
      }

      newOrderItems.forEach(item => {
        const { quantity, instructions } = item;
        let optionValueId = "";

        if (item.options && item.options.value && item.options.value.id) {
          optionValueId = item.options.value.id;
        }

        this.addItemToOrder({
          id: item.id,
          quantity,
          instructions,
          optionValueId
        });
      });

      this.$router
        .push({ name: "App: Order Review and Confirm" })
        .catch(e => e);
    },

    handleLocationPicker() {
      this.$router.push({ name: "App: Locations" }).catch(e => e);
    }
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("order", ["openOrders", "orderLocation"])
  }
};
</script>
